import { useMemo } from "react";
import { useSelector } from "react-redux";

import { Grid, makeStyles, Typography, Box } from "@material-ui/core";

import {
  nFormatter,
  floatFormatter,
  currencyFormatter,
  nKilowattFormatter,
  getLabel as toolTipLabel,
} from "../../services";
import { COLORS } from "../../theme/colors";
import { ColorIndicator } from "../colorIndicator";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: COLORS.darks[0][100],
    borderRadius: 8,
    padding: `${theme.spacing(1)}px ${theme.spacing(1.5)}px`,
    width: ({ maxWidthDatakey }) => {
      return maxWidthDatakey < 10 ? 140 : 180;
    },
    zIndex: 2,
  },
  smallText: {
    color: COLORS.white,
    fontWeight: "bold",
    fontSize: 10,
    "&:first-letter": {
      textTransform: "capitalize",
    },
  },
  bigText: {
    color: COLORS.white,
    fontWeight: "bold",
    fontSize: 12,
  },
  downArrow: {
    width: 10,
    height: 10,
    backgroundColor: COLORS.darks[0][100],
    transform: "rotate(45deg)",
    position: "relative",
    bottom: 5,
  },
}));

export const TooltipContent = ({
  active,
  payload,
  period,
  hideSum,
  dateDiff,
  defaultLabel,
  showDots,
}) => {
  const classes = useStyles({
    maxWidthDatakey: payload
      ? payload.reduce(
          (a, b) => {
            if (!b.value) return a;
            if (a.dataKey.length > b.dataKey.length) {
              return a;
            }
            return b;
          },
          { dataKey: "" }
        ).dataKey.length
      : null,
  });
  const { user } = useSelector((state) => state.auth);
  const currencySymbol = user.currency.symbol;
  const DEFAULT_UNIT = currencySymbol;
  const sum = floatFormatter(
    payload ? payload.reduce((accumulator, curr) => accumulator + curr.value, 0) : 0
  );
  const getLabel = () => {
    if (payload.length && payload[0].payload) {
      if (payload[0].payload.date) {
        const date = new Date(payload[0].payload.date);
        if (period?.name === "20Y" || dateDiff >= 365) {
          return `${date.getUTCFullYear()}`;
        }
        if (period?.name === "Acc.") {
          return payload[0].payload?.x;
        }
        return `${date.getUTCFullYear()}-${date.getUTCMonth() + 1}`;
      } else if (payload[0].payload.name) {
        return payload[0].payload.name;
      }
      return payload[0].payload?.x;
    }
    return;
  };
  const getValue = (unit, value) => {
    if (unit) {
      switch (unit) {
        case "kwh":
          return nKilowattFormatter(value);
        case currencySymbol:
          return `${DEFAULT_UNIT}${nFormatter(value)}`;
        case "nFormatter":
          return nFormatter(value);
        case "%":
          return `${floatFormatter(value)} ${unit}`;
        case "unitSystem":
          return `${nFormatter(value)} ${user?.unit_system}`;
        case "perSquare":
          return `${currencyFormatter(value, currencySymbol)}/${user?.unit_system}`;
        default:
          return `${nFormatter(value)} ${unit}`;
      }
    }

    return `${DEFAULT_UNIT}${nFormatter(value)}`;
  };

  const getUnit = useMemo(() => {
    return payload[0]?.payload.unit;
  }, [payload]);

  if (active && payload && payload.length && !payload[0].payload.hide) {
    return (
      <Grid container direction="column" alignItems="center">
        <Box className={classes.root}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography className={classes.bigText}>{defaultLabel || getLabel()}</Typography>
            {payload.length > 1 && !hideSum && (
              <Typography className={classes.smallText}>
                {getValue(getUnit || payload[0].unit, sum)}
              </Typography>
            )}
          </Grid>

          {payload.map((entry, index) => {
            if (entry.value !== null && entry.chartType !== "hide") {
              return (
                <Grid key={index} container justifyContent="space-between" alignItems="center">
                  <Grid
                    container
                    alignItems="center"
                    style={{ width: "fit-content", maxWidth: "70%" }}
                  >
                    <ColorIndicator
                      color={entry.color}
                      onlyBorder={showDots && entry.dataKey === showDots}
                      style={{ marginRight: 4 }}
                    />

                    <Typography className={classes.smallText}>
                      {toolTipLabel(entry.name)}
                    </Typography>
                  </Grid>
                  <Typography className={classes.smallText}>
                    {getValue(getUnit || entry.unit, entry.value)}
                  </Typography>
                </Grid>
              );
            }
            return null;
          })}
        </Box>

        <Box className={classes.downArrow}></Box>
      </Grid>
    );
  }
  return null;
};
